/**
 * Per-demo architecture sketches for the walkthrough page — diagram-as-code (hand-authored inline
 * SVG, same approach as PatternDiagram), themed off the CSS vars. The diagram sits above the steps
 * and highlights the components/edges the current step touches, continuously answering "where in the
 * picture are we?". Unknown demo ids render nothing, so new demos never break the page.
 *
 * STEP_TOUCHES maps walkthrough/teardown step ids → diagram element ids; a step missing from the map
 * simply highlights nothing. Adding a demo = one new case + its map.
 */

const STEP_TOUCHES: Record<string, Record<string, string[]>> = {
  bakehouse: {
    intro: ['booth', 'aistor', 'dbx'],
    setup: ['aistor'],
    customers: ['booth', 'aistor', 'e-write'],
    'transactions-1': ['booth', 'aistor', 'e-write'],
    'publish-share': ['aistor', 'e-share'],
    'databricks-setup': ['dbx', 'e-share'],
    'databricks-query-1': ['dbx', 'e-share', 'aistor'],
    'transactions-2': ['booth', 'aistor', 'e-write'],
    'databricks-query-2': ['dbx', 'e-share', 'aistor'],
    reset: ['booth', 'aistor', 'e-write'],
    'teardown-intro': ['dbx', 'aistor'],
    'databricks-teardown': ['dbx'],
    'share-teardown': ['aistor', 'e-share'],
    'teardown-done': ['booth', 'aistor', 'dbx'],
  },
  'aistor-capabilities': {
    intro: ['client', 'lab', 'lab2', 'tier', 'obs'],
    setup: ['client', 'lab', 'e-s3'],
    iam: ['client', 'lab', 'e-s3'],
    sts: ['client', 'lab', 'e-s3'],
    tiering: ['lab', 'tier', 'e-tier'],
    'tiering-verify': ['lab', 'tier', 'e-tier'],
    replication: ['lab', 'lab2', 'e-repl'],
    anonymous: ['client', 'lab', 'e-s3'],
    'qos-intro': ['client', 'lab', 'e-s3'],
    'qos-baseline': ['client', 'lab', 'e-s3'],
    'qos-limit-50': ['lab'],
    'qos-run-50': ['client', 'lab', 'e-s3'],
    'qos-limit-100': ['lab'],
    'qos-run-100': ['client', 'lab', 'e-s3'],
    observability: ['obs', 'e-scrape', 'lab', 'lab2', 'tier'],
    'teardown-intro': ['lab'],
    teardown: ['lab', 'lab2', 'tier'],
  },
};

export function DemoDiagram({ demoId, activeStepId }: { demoId: string; activeStepId: string | null }) {
  const touches = STEP_TOUCHES[demoId];
  if (!touches) return null;
  const active = new Set(activeStepId ? (touches[activeStepId] ?? []) : []);
  const on = (id: string, base: string) => `${base} ${active.has(id) ? 'on' : ''}`;

  if (demoId === 'bakehouse') {
    return (
      <figure className="dd" aria-label="Bakehouse demo architecture">
        <svg viewBox="0 0 660 158">
          {/* Booth */}
          <g className={on('booth', 'dd-g')}>
            <rect className="dd-box" x="16" y="46" width="150" height="64" rx="10" />
            <text className="dd-t" x="91" y="72" textAnchor="middle">Booth / laptop</text>
            <text className="dd-s" x="91" y="90" textAnchor="middle">demo runner · sales CSVs</text>
          </g>
          {/* AIStor */}
          <g className={on('aistor', 'dd-g')}>
            <rect className="dd-node" x="252" y="26" width="166" height="104" rx="10" />
            <text className="dd-t" x="335" y="50" textAnchor="middle">Your AIStor</text>
            <rect className="dd-chip" x="268" y="62" width="134" height="24" rx="6" />
            <text className="dd-s" x="335" y="78" textAnchor="middle">Iceberg REST catalog</text>
            <rect className="dd-chip" x="268" y="94" width="134" height="24" rx="6" />
            <text className="dd-s" x="335" y="110" textAnchor="middle">Delta Sharing server</text>
          </g>
          {/* Databricks */}
          <g className={on('dbx', 'dd-g')}>
            <rect className="dd-box" x="500" y="46" width="144" height="64" rx="10" />
            <text className="dd-t" x="572" y="72" textAnchor="middle">Databricks</text>
            <text className="dd-s" x="572" y="90" textAnchor="middle">provider → catalog</text>
          </g>
          {/* Booth → AIStor: governed Iceberg writes */}
          <g className={on('e-write', 'dd-g')}>
            <line className="dd-edge" x1="166" y1="78" x2="244" y2="78" />
            <polygon className="dd-arrow" points="252,78 242,73 242,83" />
            <text className="dd-el" x="205" y="68" textAnchor="middle">Iceberg writes</text>
          </g>
          {/* AIStor → Databricks: live Delta Sharing read */}
          <g className={on('e-share', 'dd-g')}>
            <line className="dd-edge dashed" x1="418" y1="78" x2="492" y2="78" />
            <polygon className="dd-arrow" points="500,78 490,73 490,83" />
            <text className="dd-el" x="459" y="68" textAnchor="middle">Delta Sharing</text>
            <text className="dd-el" x="459" y="98" textAnchor="middle">live read · no copy</text>
          </g>
          {/* Zone captions */}
          <text className="dd-zone" x="91" y="130" textAnchor="middle">the booth</text>
          <text className="dd-zone" x="335" y="150" textAnchor="middle">your store — data never leaves</text>
          <text className="dd-zone" x="572" y="130" textAnchor="middle">their cloud · reads over a token</text>
        </svg>
      </figure>
    );
  }

  if (demoId === 'aistor-capabilities') {
    return (
      <figure className="dd" aria-label="AIStor capabilities lab architecture">
        <svg viewBox="0 0 660 196">
          {/* Client */}
          <g className={on('client', 'dd-g')}>
            <rect className="dd-box" x="16" y="70" width="138" height="56" rx="10" />
            <text className="dd-t" x="85" y="93" textAnchor="middle">mc · runner · WARP</text>
            <text className="dd-s" x="85" y="111" textAnchor="middle">the commands you run</text>
          </g>
          {/* lab primary */}
          <g className={on('lab', 'dd-g')}>
            <rect className="dd-node" x="240" y="18" width="132" height="56" rx="10" />
            <text className="dd-t" x="306" y="41" textAnchor="middle">lab · primary</text>
            <text className="dd-s" x="306" y="59" textAnchor="middle">IAM · STS · QoS · ILM</text>
          </g>
          {/* lab2 peer */}
          <g className={on('lab2', 'dd-g')}>
            <rect className="dd-node" x="240" y="126" width="132" height="52" rx="10" />
            <text className="dd-t" x="306" y="148" textAnchor="middle">lab2 · peer</text>
            <text className="dd-s" x="306" y="165" textAnchor="middle">active-active</text>
          </g>
          {/* tier */}
          <g className={on('tier', 'dd-g')}>
            <rect className="dd-store" x="486" y="18" width="132" height="56" rx="10" />
            <text className="dd-t" x="552" y="41" textAnchor="middle">tier · warm target</text>
            <text className="dd-s" x="552" y="59" textAnchor="middle">separate deployment</text>
          </g>
          {/* observability */}
          <g className={on('obs', 'dd-g')}>
            <rect className="dd-box" x="486" y="126" width="158" height="52" rx="10" />
            <text className="dd-t" x="565" y="148" textAnchor="middle">Prometheus → Grafana</text>
            <text className="dd-s" x="565" y="165" textAnchor="middle">curated AIStor boards</text>
          </g>
          {/* client → lab */}
          <g className={on('e-s3', 'dd-g')}>
            <line className="dd-edge" x1="154" y1="90" x2="232" y2="56" />
            <polygon className="dd-arrow" points="240,52 229,52 233,62" />
            <text className="dd-el" x="188" y="60" textAnchor="middle">S3 API</text>
          </g>
          {/* lab ⇄ lab2 site replication */}
          <g className={on('e-repl', 'dd-g')}>
            <line className="dd-edge" x1="306" y1="82" x2="306" y2="118" />
            <polygon className="dd-arrow" points="306,74 301,84 311,84" />
            <polygon className="dd-arrow" points="306,126 301,116 311,116" />
            <text className="dd-el" x="318" y="104">site replication</text>
          </g>
          {/* lab → tier ILM */}
          <g className={on('e-tier', 'dd-g')}>
            <line className="dd-edge" x1="372" y1="46" x2="478" y2="46" />
            <polygon className="dd-arrow" points="486,46 476,41 476,51" />
            <text className="dd-el" x="428" y="36" textAnchor="middle">ILM tiering</text>
          </g>
          {/* prometheus scrapes all three */}
          <g className={on('e-scrape', 'dd-g')}>
            <line className="dd-edge dashed" x1="512" y1="126" x2="360" y2="74" />
            <line className="dd-edge dashed" x1="486" y1="152" x2="372" y2="152" />
            <line className="dd-edge dashed" x1="552" y1="126" x2="552" y2="74" />
            <text className="dd-el" x="452" y="112" textAnchor="middle">scrape · metrics v3</text>
          </g>
        </svg>
      </figure>
    );
  }

  return null;
}
